import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface Currency {
  code: string;
  symbol: string;
  name: string;
  rate: number;
}

export const currencies: Currency[] = [
  { code: "USD", symbol: "$", name: "US Dollar", rate: 1 },
  { code: "EUR", symbol: "€", name: "Euro", rate: 0.92 },
  { code: "GBP", symbol: "£", name: "British Pound", rate: 0.79 },
  { code: "JPY", symbol: "¥", name: "Japanese Yen", rate: 149.50 },
  { code: "CHF", symbol: "CHF", name: "Swiss Franc", rate: 0.88 },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar", rate: 1.36 },
  { code: "AUD", symbol: "A$", name: "Australian Dollar", rate: 1.53 },
  { code: "CNY", symbol: "¥", name: "Chinese Yuan", rate: 7.24 },
  { code: "INR", symbol: "₹", name: "Indian Rupee", rate: 83.12 },
  { code: "MXN", symbol: "$", name: "Mexican Peso", rate: 17.15 },
  { code: "BRL", symbol: "R$", name: "Brazilian Real", rate: 4.97 },
  { code: "KRW", symbol: "₩", name: "South Korean Won", rate: 1320.50 },
  { code: "SAR", symbol: "﷼", name: "Saudi Riyal", rate: 3.75 },
  { code: "AED", symbol: "د.إ", name: "UAE Dirham", rate: 3.67 },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar", rate: 1.34 },
  { code: "HKD", symbol: "HK$", name: "Hong Kong Dollar", rate: 7.82 },
  { code: "SEK", symbol: "kr", name: "Swedish Krona", rate: 10.42 },
  { code: "NOK", symbol: "kr", name: "Norwegian Krone", rate: 10.65 },
  { code: "DKK", symbol: "kr", name: "Danish Krone", rate: 6.87 },
  { code: "PLN", symbol: "zł", name: "Polish Zloty", rate: 3.98 },
  { code: "TRY", symbol: "₺", name: "Turkish Lira", rate: 32.15 },
  { code: "ZAR", symbol: "R", name: "South African Rand", rate: 18.65 },
  { code: "RUB", symbol: "₽", name: "Russian Ruble", rate: 92.50 },
  { code: "NZD", symbol: "NZ$", name: "New Zealand Dollar", rate: 1.64 },
];

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  convertAmount: (amountInUSD: number) => number;
  formatAmount: (amountInUSD: number) => string;
  isLoading: boolean;
  lastUpdated: string | null;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const [currency, setCurrencyState] = useState<Currency>(currencies[0]);
  const [liveRates, setLiveRates] = useState<Record<string, number> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  // Fetch live rates on mount
  useEffect(() => {
    fetchLiveRates();
    const savedCurrency = localStorage.getItem("preferredCurrency");
    if (savedCurrency) {
      const found = currencies.find(c => c.code === savedCurrency);
      if (found) setCurrencyState(found);
    }
  }, []);

  const fetchLiveRates = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-exchange-rates');
      if (!error && data?.rates) {
        setLiveRates(data.rates);
        setLastUpdated(data.last_updated || new Date().toISOString());
      }
    } catch (err) {
      console.error("Failed to fetch live rates:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const setCurrency = (newCurrency: Currency) => {
    setCurrencyState(newCurrency);
    localStorage.setItem("preferredCurrency", newCurrency.code);
  };

  const convertAmount = (amountInUSD: number): number => {
    // Use live rates if available, otherwise fallback to static rates
    const rate = liveRates?.[currency.code] ?? currency.rate;
    return amountInUSD * rate;
  };

  const formatAmount = (amountInUSD: number): string => {
    const converted = convertAmount(amountInUSD);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(converted);
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, convertAmount, formatAmount, isLoading, lastUpdated }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
};
